import _ from "lodash";
import { POLYGON_ELEMENT_CONFIG } from "../KonvaConfig";
import { KONVA_ATTR, KONVA_EVENTS } from "../KonvaConstants";
import {
  generateElementName,
  getCanvasRelativeSize,
  listenBackEvents,
  listenDocumentEvent,
} from "../../KonvaCommonUtils";
import { generateRandomId } from "Utils";

/**
 *
 * @param {*} item
 * @param {*} konva
 * @returns New shape instance
 */
export const createPolygonNode = (
  item,
  { mode, questionType, layer, transformer, konva } = {}
) => {
  const strokeWidth = getCanvasRelativeSize(
    POLYGON_ELEMENT_CONFIG.STROKE_WIDTH
  );
  const props = {
    closed: true,
    id: _.get(item, "id"),
    x: _.get(item, "x"),
    y: _.get(item, "y"),
    name: generateElementName(item),
    draggable: _.get(item, "draggable", false),
    dash: [strokeWidth, strokeWidth],
    strokeWidth: strokeWidth,
    stroke: POLYGON_ELEMENT_CONFIG.STROKE_COLOR,
    points: _.get(item, "points", POLYGON_ELEMENT_CONFIG.DEFAULT_POINTS),
  };

  if (mode === "create") {
    props.fillEnabled = false;
    props.hitStrokeWidth = Math.min(strokeWidth * 9, 20);
  }

  const dash = {
    ...props,
    name: props.id + "-dash-support",
    id: props.id + "-dash-support",
    stroke: "white",
    draggable: false,
    listening: false,
  };
  delete dash.dash;

  const supportDashLine = new konva.Line(dash);
  layer.add(supportDashLine);

  const node = new konva.Line(props);
  layer.add(node);

  const updateSupportDashLine = () => {
    supportDashLine.setAttrs(node.attrs);
    supportDashLine.dash([10, 0]);
    supportDashLine.stroke("white");
  };

  if (transformer) {
    node.on(KONVA_EVENTS.DRAGMOVE, updateSupportDashLine);
    transformer.on(KONVA_EVENTS.TRANSFORM, updateSupportDashLine);
  }

  if (questionType?.isDragAndDrop) {
    // * adding correct element as attributes
    node.setAttr(
      KONVA_ATTR.DROPZONE_TAGGED_ANSWER,
      _.get(item, "correctElements", [])
    );
  }

  return node;
};

/**
 *
 * @param {*} props
 * @description draw ellipse
 */
export const drawPolygonEventListener = props => {
  const { konva, stage, layer, transformer } = props;

  // * Updating drawing cursor
  stage.container().style.cursor = "crosshair";
  const cursorImage = new Image();
  cursorImage.src = POLYGON_ELEMENT_CONFIG.DRAWING_CURSOR_SRC;
  cursorImage.onload = function () {
    stage.container().style.cursor = `url(${cursorImage.src}), auto`;
  };

  let polygon, startPos, startingPoint, lastLine, supportDashLine;

  /**
   * cancelling drawing event
   */
  const cancelDrawing = e => {
    if (!stage.container().contains(e.target)) {
      stage.setAttr(KONVA_ATTR.IS_DRAWING_POLYGON, false);
      stage.container().style.cursor = "default";

      if (_.isFunction(props?.onDrawCancel)) props.onDrawCancel();
      if (_.isFunction(cleanClickEvent)) cleanClickEvent();
    }
  };
  const cleanClickEvent = listenDocumentEvent("click", cancelDrawing);

  /**
   * adding first point of first click
   * adding next points of polygon and listening close shape event and sending callback
   */
  const onClick = () => {
    const pos = stage.getPointerPosition();
    startPos = pos;

    const strokeWidth = POLYGON_ELEMENT_CONFIG.STROKE_WIDTH;

    if (!polygon) {
      supportDashLine = new konva.Line({
        points: [pos.x, pos.y],
        draggable: false,
        id: generateRandomId(),
        stroke: "white",
        strokeWidth: strokeWidth,
      });
      layer.add(supportDashLine);

      polygon = new konva.Line({
        points: [pos.x, pos.y],
        draggable: false,
        id: generateRandomId(),
        stroke: POLYGON_ELEMENT_CONFIG.STROKE_COLOR,
        strokeWidth: strokeWidth,
        dash: [strokeWidth, strokeWidth],
      });
      layer.add(polygon);

      startingPoint = new konva.Circle({
        id: generateRandomId(),
        x: pos.x,
        y: pos.y,
        radius: 5,
        fill: POLYGON_ELEMENT_CONFIG.DRAW_MODE_WHITE,
        strokeWidth: getCanvasRelativeSize(
          POLYGON_ELEMENT_CONFIG.DRAW_MODE_STROKE_WIDTH
        ),
        stroke: POLYGON_ELEMENT_CONFIG.DRAW_MODE_STROKE_COLOR,
      });
      layer.add(startingPoint);

      startingPoint.on(KONVA_EVENTS.MOUSEOVER, () => {
        startingPoint.fill(POLYGON_ELEMENT_CONFIG.DRAW_MODE_STROKE_COLOR);
        layer.batchDraw();
      });

      startingPoint.on(KONVA_EVENTS.MOUSEOUT, () => {
        startingPoint.fill(POLYGON_ELEMENT_CONFIG.DRAW_MODE_WHITE);
        layer.batchDraw();
      });

      startingPoint.on(KONVA_EVENTS.CLICK, () => {
        const points = polygon.points();
        if (points?.length < 3) return;

        polygon = null;
        startPos = null;
        lastLine = null;
        stage.container().style.cursor = "default";
        stage.setAttr(KONVA_ATTR.IS_DRAWING_POLYGON, false);
        if (_.isFunction(cleanClickEvent)) cleanClickEvent();

        startingPoint.off();
        startingPoint.remove();

        if (_.isFunction(props?.onDrawComplete)) props.onDrawComplete(points);
      });
    } else {
      if (polygon instanceof konva.Line) {
        polygon.points([...polygon.points(), pos.x, pos.y]);
        supportDashLine.points([...supportDashLine.points(), pos.x, pos.y]);
        const newPoint = new konva.Circle({
          id: generateRandomId(),
          x: pos.x,
          y: pos.y,
          radius: 5,
          fill: POLYGON_ELEMENT_CONFIG.DRAW_MODE_WHITE,
          stroke: POLYGON_ELEMENT_CONFIG.DRAW_MODE_STROKE_COLOR,
          strokeWidth: getCanvasRelativeSize(
            POLYGON_ELEMENT_CONFIG.DRAW_MODE_STROKE_WIDTH
          ),
        });
        layer.add(newPoint);
        startingPoint?.moveToTop();
      }
    }
  };

  /**
   * showing line wrt to mouse position and last clicked point
   */
  const onMouseMove = () => {
    if (!polygon || !startPos) return;

    const pos = stage.getPointerPosition();
    const points = polygon.points();
    if (_.isEmpty(points)) return;

    const id = generateRandomId();
    const lastX = points[_.size(points) - 2];
    const lastY = points[_.size(points) - 1];

    if (lastLine) lastLine.destroy();

    lastLine = new konva.Line({
      points: [lastX, lastY, pos.x, pos.y],
      draggable: false,
      id: id,
      name: id,
      stroke: POLYGON_ELEMENT_CONFIG.DRAW_MODE_STROKE_COLOR,
      strokeWidth: POLYGON_ELEMENT_CONFIG.DRAW_MODE_STROKE_WIDTH,
    });

    layer.add(lastLine);
    layer.batchDraw();
  };

  listenBackEvents(stage, transformer, {
    onClick,
    onMouseMove,
  });

  return {};
};

/**
 *
 * @param {*} node
 * @param {*} konva
 * @returns is ellipse node or not
 */
export const isPolygonNode = (node, konva) => {
  return node && node instanceof konva.Line;
};
